import { Component, OnInit, OnDestroy, Input } from '@angular/core';

import { Observable, Subscription } from 'rxjs';
import { tap, filter, switchMap, combineLatestWith } from 'rxjs/operators';
import moment from 'moment-timezone';
import { MessageService } from 'primeng/api';

import { AbstractDialogComponent } from '../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { TableColumnModel } from '../../../../Common/Forms/models/table-column.model';
import { styleColumnsConfig } from '../../config/stylesTable/style-columns.config';
import { styleNameColumnConfig } from '../../config/stylesTable/style-name-column.config';
import { StylesService } from '../../services/styles.service';

import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { Style } from '../../models/style.model';
import { StyleRowModel } from '../../models/style-row.model';
import { ConfirmDialogModel } from '../../../../Common/Dialogs/models/confirm-dialog.model';
import { ConfirmDialogComponent } from '../../../../Common/Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { UsersService } from '../../../../Core/Users/services/users.service';
import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { FullProfileAnimationModel } from '../../../../Core/Dialogs/models/full-profile-animation.model';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { UserModel } from '../../../../Core/Users/models/user.model';


@Component({
    selector: 'agpr-manage-styles-dialog',
    templateUrl: './manage-styles-dialog.component.html',
    styleUrls: ['./manage-styles-dialog.component.scss'],
    standalone: false
})
export class ManageStylesDialogComponent extends AbstractDialogComponent implements OnInit, OnDestroy {

    @Input() overlayContainer: Element;
    @Input() animationTrigger: Observable<FullProfileAnimationModel>;

    hideSlideOverlay: boolean = false;

    allStyles: Style[];
    styleRows: StyleRowModel[];
    hasSelectedStyles: boolean;
    dateFormat: string;

    private subscriptions: Subscription[] = [];

    readonly buttonTypes = ButtonTypesEnum;
    readonly styleColumnDefs: TableColumnModel[] = styleColumnsConfig;
    readonly primaryColumnDef: TableColumnModel = styleNameColumnConfig;

    constructor(
        public coreDialogService: CoreDialogService,
        private stylesService: StylesService,
        private localizationService: LocalizationService,
        private usersService: UsersService,
        private userStateService: CurrentUserStateService,
        private messageService: MessageService
    ) {
        super(coreDialogService, 'ManageStyles');
    }

    ngOnInit() {
        this.subscriptions.push(
            this.stylesService.all$.pipe(
                combineLatestWith(this.usersService.accountUsers$)
            ).subscribe(([styles, users]) => {
                this.allStyles = styles ?? [];

                this.constructStyleRows(this.allStyles, users);
            }),
            this.userStateService.getPreferences().subscribe(
                prefs => this.dateFormat = prefs.getDateFormatString(true)
            )
        );
    }


    deleteSelectedStyles() {
        const selectedStyles: Style[] = this.selectedStyles;

        this.confirmDeleteStyles(selectedStyles);
    }

    saveStyle(styleRow: StyleRowModel) {
        const style: Style = styleRow.style;

        if (styleRow.name === style.name) {

            return;
        }

        this.stylesService.isNameDuplicated$(styleRow.name).pipe(
            tap(isDuplicate => {
                if (isDuplicate) {
                    styleRow.name = style.name;

                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'Styles.manageDialog.error.duplicateName.heading',
                        detail: this.localizationService.transform('Styles.manageDialog.error.duplicateName.message', {name: styleRow.name})
                    });
                }
            }),
            filter(x => !x),
            switchMap(() => {
                style.name = styleRow.name;
                return this.stylesService.update(style);
            })
        ).subscribe();
    }

    deleteStyle(styleRow: StyleRowModel): void {
        const style: Style = styleRow.style;
        const styles: Style[] = [style];

        this.confirmDeleteStyles(styles);
    }

    checkForSelected(): void {
        this.hasSelectedStyles = this.styleRows.some(x => x.selected);
    }

    private confirmDeleteStyles(styles: Style[]): void {
        const confirmConfig: ConfirmDialogModel = {
            inputs: {
                id: 'manageStylesDialog.confirmDialog',
                heading: 'Styles.manageDialog.confirmDelete.heading',
                alertMessage: 'Styles.manageDialog.confirmDelete.alert',
                alertType: messageSeverityEnum.warning,
                message: 'Styles.manageDialog.confirmDelete.message',
                list: styles.map(x => x.name),
                okLabel: 'delete'
            },
            outputs: {
                dialogClose: confirmed => {
                    if (confirmed) {
                        this.stylesService.bulkDelete(styles).subscribe();
                    }
                }
            }
        };
        this.coreDialogService.open(ConfirmDialogComponent, confirmConfig);
    }

    private constructStyleRows(styles: Style[], users: UserModel[]) {

        const findUserName = (id: number) => users.find(u => u.id === id)?.getDisplayName() ?? null;

        this.styleRows = styles.map(style => {
            const styleRow: StyleRowModel = new StyleRowModel(style);
            styleRow.setType(this.getLocalizeStyleType(style));
            styleRow.tooltipInfo = this.localizationService.transform('Briefings.templateManageDialog.tooltipInfo', {
                createdBy: findUserName(style.createdByUserId),
                createdDate: moment(style.createdDate).format(this.dateFormat),
                modifiedBy: findUserName(style.lastModifiedByUserId ? style.lastModifiedByUserId : style.createdByUserId),
                modifiedDate: moment(style.lastModifiedDate ? style.lastModifiedDate : style.createdDate).format(this.dateFormat),
            });

            return styleRow;
        });
    }

    private getLocalizeStyleType(style: Style): string {
        const hasType: boolean = style.type !== null && style.type !== undefined;
        let localizedType: string = null;

        if (hasType) {
            localizedType = this.localizationService.transform(`Style.manageDialog.type${style.type}`);
        }

        return localizedType;
    }

    private get selectedStyles(): Style[] {
        const selectedStyles: Style[] = this.selectedStyleRows.map(
            styleRow => {
                const style: Style = styleRow.style;

                return style;
            }
        );

        return selectedStyles;
    }

    private get selectedStyleRows(): StyleRowModel[] {
        const selectedStyleRows: StyleRowModel[] = this.styleRows.filter(
            styleRow => {
                const isSelected: boolean = styleRow.selected;

                if (isSelected) {
                    return true;
                }
            });

        return selectedStyleRows;
    }

    cancel() {
        this.hideSlideOverlay = true;
        this.closeDialog();
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
